import { useCloneTicket, useProjects } from '@servicetim/ui/api';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  ProblemBanner,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@servicetim/ui/components';
import { useNavigate } from '@tanstack/react-router';
import { LoaderCircle } from 'lucide-react';
import { useId, useState } from 'react';
import { useTicketContext } from './ticket-context';

/** Clone (S-05-12): same or another project, optionally with the comments. */
export function CloneDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { ticket } = useTicketContext();
  const navigate = useNavigate();
  const projects = useProjects({ pageSize: 200 });
  const [projectId, setProjectId] = useState(ticket.projectId);
  const [includeComments, setIncludeComments] = useState(false);
  const clone = useCloneTicket();
  const projectSelectId = useId();
  const commentsId = useId();

  const submit = async () => {
    const created = await clone.mutateAsync({
      id: ticket.id,
      projectId: projectId === ticket.projectId ? undefined : projectId,
      includeComments,
    });
    toast.success(`Cloned as ${created.key}`);
    onOpenChange(false);
    void navigate({ to: '/tickets/$idOrKey', params: { idOrKey: created.key } });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Clone {ticket.key}</DialogTitle>
          <DialogDescription>
            Copies summary, description, fields and labels into a new ticket linked as a clone.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor={projectSelectId}>Target project</Label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger id={projectSelectId}>
                <SelectValue placeholder={projects.isPending ? 'Loading…' : 'Select…'} />
              </SelectTrigger>
              <SelectContent>
                {projects.items
                  .filter((p) => p.archivedAt === null || p.id === ticket.projectId)
                  .map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.key} · {p.name}
                      {p.id === ticket.projectId ? ' (current)' : ''}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id={commentsId}
              checked={includeComments}
              onCheckedChange={(v) => setIncludeComments(v === true)}
            />
            <Label htmlFor={commentsId} className="font-normal">
              Include comments
            </Label>
          </div>
          {clone.error ? <ProblemBanner error={clone.error} size="sm" /> : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={clone.isPending}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={clone.isPending}>
            {clone.isPending ? <LoaderCircle className="animate-spin" /> : null}
            Clone
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
